import React, { useState, useEffect, useMemo, useCallback } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import axios from "axios";
import "../Style/Member.css";
import { useFilterContext } from "./FilterContext";
import { BASE_URL } from "../Apis/apiConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const defaultRowsPerPage = 10;

const Member = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [view, setView] = useState(null);
  const [activeMember, setActiveMember] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const { filteredData } = useFilterContext();
  const token = localStorage.getItem("token");
  const society = (localStorage.getItem("societyName") || localStorage.getItem("society") || "").trim();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!token) {
        setError("Not logged in");
        setData([]);
        return;
      }
      if (!society) {
        setError("Society not set");
        setData([]);
        return;
      }

      const resp = await axios.get(`${BASE_URL}/api/member/get-list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { society },
      });

      const payload = resp?.data ?? [];
      if (!Array.isArray(payload)) {
        if (Array.isArray(payload?.data)) {
          setData(payload.data);
        } else {
          setData([]);
          setError("No data received from server");
        }
      } else {
        setData(payload);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch members");
      toast.error("Failed to fetch members");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [society, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const source = useMemo(() => (filteredData && filteredData.length > 0 ? filteredData : data), [filteredData, data]);
  const extractName = (member) => {
    if (!member) return "";
    if (member.Member_Name) return member.Member_Name;
    if (member.name) return member.name;
    if (member.FullName) return member.FullName;
    if (member.full_name) return member.full_name;
    if (member.firstName && member.lastName) return `${member.firstName} ${member.lastName}`;
    for (const k of Object.keys(member)) {
      const v = member[k];
      if (typeof v === "string" && v.trim()) return v;
    }
    return "";
  };
  const extractBillNo = (member) => {
    if (!member) return "";
    const candidates = ["Bill_No.", "Bill_No", "BillNo", "billNo", "bill_no", "billno", "BillNumber", "billNumber"];
    for (const k of candidates) if (member[k]) return String(member[k]);
    for (const k of Object.keys(member)) if (/bill/i.test(k) && member[k]) return String(member[k]);
    return "";
  };

  const extractMemberId = (member) => {
    return member?.Member_ID ?? member?.id ?? member?.Sr_No ?? member?.Sr ?? "";
  };

  const totalRows = source.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (currentPage < 1) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [source]);

  const pageStartIndex = (currentPage - 1) * rowsPerPage;
  const visibleRows = source.slice(pageStartIndex, pageStartIndex + rowsPerPage);

  const openView = async (viewType, member) => {
    setView(viewType);
    setActiveMember(member);
    setViewData(null);
    setViewLoading(true);
    try {
      const memberId = extractMemberId(member) || "";
      const billNo = extractBillNo(member) || "";
      const rawSoc = localStorage.getItem("societyName") ?? localStorage.getItem("society") ?? "";
      const safeSoc = (String(rawSoc).trim().toLowerCase().replace(/\s+/g, "_")) || "society";
      const table = `${safeSoc}_bill`;

      const resp = await axios.get(`${BASE_URL}/api/member/get-bill`, {
        params: { memberId, table, billNo, view: viewType },
        headers: { Authorization: `Bearer ${token}` },
      });

      setViewData(resp.data ?? null);
    } catch (err) {
      console.warn("Couldn't fetch view data (optional):", err);
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };
  const closeView = () => {
    setView(null);
    setActiveMember(null);
    setViewData(null);
    setViewLoading(false);
  };
  const goToFirst = () => setCurrentPage(1);
  const goToLast = () => setCurrentPage(totalPages);
  const prevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const nextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
  const goToPage = (n) => setCurrentPage(() => {
    const nn = Number(n) || 1;
    return Math.min(Math.max(1, nn), totalPages);
  });

  const showingFrom = totalRows === 0 ? 0 : pageStartIndex + 1;
  const showingTo = Math.min(totalRows, pageStartIndex + visibleRows.length);
  const InPagePanel = ({ type, member, data, loading, onClose }) => {
    const memberName = extractName(member) || "Member";
    const memberId = extractMemberId(member) || "";
    const billNo = extractBillNo(member) || "";

    const titleMap = {
      bill: "Bill",
      ledger: "Ledger",
      credit: "Credit Note",
      debit: "Debit Note",
      receipt: "Receipt",
    };
    const title = titleMap[type] || "Detail";

    return (
      <div style={{ marginTop: 12 }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 12
        }}>
          <div>
            <button onClick={onClose} style={{
              border: "none",
              background: "transparent",
              color: "#0b5ed7",
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: 8
            }}>← Back to list</button>

            <h2 style={{ margin: "8px 0 4px 0" }}>{title} — {memberName}</h2>
            <div style={{ color: "#64748b", fontSize: 13 }}>
              ID: {memberId} {billNo ? `• Bill No: ${billNo}` : ""}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button style={pillStyle} onClick={() => toast.info("Download (not implemented)")}>Download</button>
            <button style={pillStyle} onClick={() => toast.info("Print (not implemented)")}>Print</button>
            <button style={pillPrimaryStyle} onClick={() => toast.success("Pay Now (stub)")}>Pay Now</button>
          </div>
        </div>

        <div style={{
          background: "white",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 6px 18px rgba(16,24,40,0.04)"
        }}>
          {loading ? (
            <div>Loading {title.toLowerCase()}...</div>
          ) : data ? (
            <div>
              <h4 style={{ marginTop: 0 }}>Fetched {title} Data</h4>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{JSON.stringify(data, null, 2)}</pre>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 12 }}>
                <strong>Summary</strong>
                <div style={{ color: "#6b7280", marginTop: 6 }}>
                  {type === "ledger" ? "Ledger transactions will appear here." : `This is a placeholder for the ${title.toLowerCase()}.`}
                </div>
              </div>

              <div style={{ borderTop: "1px dashed #e6eefb", paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>Description</div>
                  <div>Amount</div>
                </div>

      
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>Base Charges</div>
                  <div>₹ 1,200.00</div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>Maintenance</div>
                  <div>₹ 300.00</div>
                </div>

                <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: "#fbfdff", border: "1px solid #eef6ff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                    <div>Total Due</div>
                    <div style={{ color: "#0b5ed7" }}>₹ 1,500.00</div>
                  </div>
                  <div style={{ color: "#6b7280", marginTop: 6 }}>Due date: 30 Oct 2025</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render
  return (
    <>
      <Header />
      <div className="d-flex">
        <Sidebar />
        <div className="flex-grow-1 right-section">
          <SubHeader />

          <div className="members-page container-fluid py-3">
            <div className="members-card">
              <div className="members-card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Members {society ? `• ${society}` : ""}</h5>
                  <small className="text-muted">Showing {showingFrom} - {showingTo} of {totalRows}</small>
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <label htmlFor="rowsPerPageSelect" style={{ marginRight: 8 }}>Rows</label>
                  <select
                    id="rowsPerPageSelect"
                    value={rowsPerPage}
                    onChange={(e) => { const val = Number(e.target.value) || defaultRowsPerPage; setRowsPerPage(val); setCurrentPage(1); }}
                  >
                    {[5, 10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>

                  <div className="d-flex gap-1 align-items-center">
                    <button className="member-button prev-next" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>« First</button>
                    <button className="member-button prev-next" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>

                    <div className="page-buttons-wrap" aria-label="Page navigation" style={{ display: "flex", alignItems: "center" }}>
                      <span className="text-muted">Page</span>
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => goToPage(e.target.value)}
                        style={{ width: 64, padding: "4px 6px", margin: "0 8px" }}
                      />
                      <span className="text-muted"> / {totalPages}</span>
                    </div>

                    <button className="member-button prev-next" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
                    <button className="member-button prev-next" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
                  </div>
                </div>
              </div>

              <div className="table-container">
                {loading ? (
                  <div className="p-3">Loading...</div>
                ) : error ? (
                  <div className="p-3 text-danger">{error}</div>
                ) : source.length === 0 ? (
                  <div className="p-3 text-muted">No members found</div>
                ) : (
                  <>
            
                    {view ? (
                      <div>
                        <InPagePanel
                          type={view}
                          member={activeMember}
                          data={viewData}
                          loading={viewLoading}
                          onClose={closeView}
                        />
                      </div>
                    ) : (
                      <div className="table-scroll">
                        <table className="members-table">
                          <thead>
                            <tr>
                              <th>Member Name</th>
                              <th>Bill</th>
                              <th>Ledger</th>
                              <th>Credit</th>
                              <th>Debit</th>
                              <th>Receipt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRows.map((row, i) => {
                              const name = String(extractName(row) ?? "").trim() || `Member ${pageStartIndex + i + 1}`;
                              return (
                                <tr key={pageStartIndex + i}>
                                  <td title={name}>
                                    <div className="cell-truncate">{name}</div>
                                  </td>

                                  <td>
                                    <button
                                      className="table-action-btn"
                                      onClick={() => openView("bill", row)}
                                      aria-label={`Bill for ${name}`}
                                    >
                                      Bill
                                    </button>
                                  </td>

                                  <td>
                                    <button
                                      className="table-action-btn"
                                      onClick={() => openView("ledger", row)}
                                      aria-label={`Ledger for ${name}`}
                                    >
                                      Ledger
                                    </button>
                                  </td>

                                  <td>
                                    <button
                                      className="table-action-btn"
                                      onClick={() => openView("credit", row)}
                                      aria-label={`Credit note for ${name}`}
                                    >
                                      Credit
                                    </button>
                                  </td>

                                  <td>
                                    <button
                                      className="table-action-btn"
                                      onClick={() => openView("debit", row)}
                                      aria-label={`Debit note for ${name}`}
                                    >
                                      Debit
                                    </button>
                                  </td>

                                  <td>
                                    <button
                                      className="table-action-btn"
                                      onClick={() => openView("receipt", row)}
                                      aria-label={`Receipt for ${name}`}
                                    >
                                      Receipt
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>

      <style jsx>{`
        .table-scroll { overflow-x: auto; }
        .members-table { width: 100%; border-collapse: collapse; }
        .members-table th, .members-table td { padding: 10px 12px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
        th { color: #333; font-weight: 600; font-size: 14px; background: #fafafa; }
        .cell-truncate {
          max-width: 420px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 15px;
          color: #111827;
        }

        .table-action-btn {
          display: inline-block;
          padding: 8px 10px;
          border-radius: 8px;
          border: 1px solid rgba(0,0,0,0.08);
          background: linear-gradient(180deg,#ffffff,#f7f9fc);
          box-shadow: 0 1px 2px rgba(16,24,40,0.04);
          cursor: pointer;
          font-size: 14px;
          color: #0b5ed7;
          min-width: 76px;
          text-align: center;
          transition: transform .12s ease, box-shadow .12s ease;
        }
        .table-action-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(11,94,215,0.12); }
        .table-action-btn:focus { outline: 3px solid rgba(11,94,215,0.12); outline-offset: 2px; }

        .member-button.prev-next { padding: 6px 10px; border-radius: 8px; border: 1px solid #e2e8f0; background: white; cursor: pointer; }
        .member-button.prev-next:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Empty state styles */
        .empty-state { display: flex; gap: 20px; align-items: center; justify-content: center; padding: 34px; border-radius: 12px; background: linear-gradient(180deg, #ffffff, #fbfdff); border: 1px solid #eef6ff; text-align: center; margin: 18px; }
        .empty-illustration { flex: 0 0 auto; }
        .empty-content { max-width: 520px; }
        .empty-content h4 { margin: 0 0 8px 0; font-size: 18px; color: #0f172a; }

        @media (max-width: 900px) {
          .cell-truncate { max-width: 220px; }
          .table-action-btn { padding: 6px 8px; min-width: 64px; font-size: 13px; }
        }
        @media (max-width: 520px) {
          .members-card-header { flex-direction: column; gap: 12px; align-items: flex-start; }
          .members-table th, .members-table td { padding: 8px 10px; }
          .table-action-btn { padding: 6px 6px; font-size: 12px; min-width: 56px; }
        }
      `}</style>
    </>
  );
};

const pillStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid rgba(11,94,215,0.08)",
  background: "white",
  cursor: "pointer",
  color: "#0b3b66",
};

const pillPrimaryStyle = {
  ...pillStyle,
  background: "#0b5ed7",
  color: "white",
  border: "1px solid rgba(11,94,215,0.12)",
};

export default Member;
